import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import { getToken } from "../lib/auth";
import { useAuth } from "../lib/auth";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Card, CardHeader, CardTitle, CardContent,
} from "../components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Loader2, Shield, Users, Building2, CheckCircle2, XCircle, Clock,
  CalendarPlus, FileText, Eye, Save, ExternalLink, ArrowLeft, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDate } from "../lib/format";

function StatBox({ label, value, icon: Icon, accent = "#D16B4D" }) {
  return (
    <div className="rounded-2xl border border-[#EBE6DE] bg-white p-5 flex items-center gap-4">
      <div className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}18` }}>
        <Icon className="h-6 w-6" style={{ color: accent }} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider font-bold text-[#586D61]">{label}</div>
        <div className="font-heading text-2xl font-semibold text-[#172E21] mt-0.5 font-mono">{value}</div>
      </div>
    </div>
  );
}

function TenantsTab() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/tenants")
      .then((r) => setTenants(r.data))
      .catch(() => toast.error("Gagal memuat tenant"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const extendTrial = async (id, days) => {
    try {
      await api.post(`/admin/tenants/${id}/extend-trial`, null, { params: { days } });
      toast.success(`Trial diperpanjang ${days} hari`);
      load();
    } catch (e) {
      toast.error("Gagal memperpanjang trial");
    }
  };

  const activatePaid = async (id, months) => {
    try {
      await api.post(`/admin/tenants/${id}/activate`, null, { params: { months } });
      toast.success(`Aktivasi paid ${months} bulan berhasil`);
      load();
    } catch (e) {
      toast.error("Gagal aktivasi");
    }
  };

  const planBadge = (plan) => {
    const map = {
      trial: "bg-[#D99338]/15 text-[#A66A1F] border-[#D99338]/30",
      active: "bg-[#1B5E3F]/12 text-[#1B5E3F] border-[#1B5E3F]/30",
      free: "bg-[#082618]/8 text-[#082618] border-[#082618]/20",
      expired: "bg-[#D14D4D]/12 text-[#D14D4D] border-[#D14D4D]/30",
    };
    return map[plan] || map.trial;
  };

  if (loading) return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#D16B4D]" /></div>;

  return (
    <Card className="border-[#EBE6DE]">
      <CardHeader>
        <CardTitle className="font-heading text-xl text-[#172E21]">
          Daftar Tenant ({tenants.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table data-testid="tenants-table">
            <TableHeader>
              <TableRow className="bg-[#FDFBF7]">
                <TableHead>Tenant</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead className="text-right">Trial Berakhir</TableHead>
                <TableHead className="text-right">Sisa Hari</TableHead>
                <TableHead className="text-center">Bookings</TableHead>
                <TableHead className="text-center">Rooms</TableHead>
                <TableHead className="text-center">Users</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.map((t) => (
                <TableRow key={t.id} data-testid={`tenant-row-${t.slug}`}>
                  <TableCell>
                    <div className="font-medium text-[#172E21]">{t.name}</div>
                    <div className="text-xs text-[#586D61]">/{t.slug}</div>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex px-2 py-0.5 rounded-full border text-xs font-medium ${planBadge(t.plan)}`}>
                      {t.plan}
                    </span>
                  </TableCell>
                  <TableCell className="text-right text-sm">{formatDate(t.trial_ends_at)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    <span className={t.days_remaining <= 7 && t.plan === "trial" ? "text-[#D14D4D] font-bold" : ""}>
                      {t.days_remaining}
                    </span>
                  </TableCell>
                  <TableCell className="text-center font-mono text-sm">{t.bookings_count}</TableCell>
                  <TableCell className="text-center font-mono text-sm">{t.rooms_count}</TableCell>
                  <TableCell className="text-center font-mono text-sm">{t.users_count}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1.5 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`btn-extend-${t.slug}`}
                        onClick={() => extendTrial(t.id, 30)}
                        className="h-8 text-xs gap-1"
                      >
                        <CalendarPlus className="h-3.5 w-3.5" /> +30hari
                      </Button>
                      <Button
                        size="sm"
                        data-testid={`btn-activate-${t.slug}`}
                        onClick={() => activatePaid(t.id, 1)}
                        className="h-8 text-xs gap-1 bg-[#1B5E3F] hover:bg-[#0F4D32]"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" /> Aktivasi 1bln
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function UpgradesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [adminNotes, setAdminNotes] = useState({});

  const load = () => {
    setLoading(true);
    const url = filter === "all" ? "/admin/upgrades" : `/admin/upgrades?status=${filter}`;
    api.get(url)
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Gagal memuat data upgrade"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filter]);

  const approve = async (id) => {
    try {
      await api.post(`/admin/upgrades/${id}/approve`);
      toast.success("Upgrade disetujui, tenant aktif");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal approve");
    }
  };

  const reject = async (id) => {
    try {
      await api.post(`/admin/upgrades/${id}/reject`, null, {
        params: { admin_notes: adminNotes[id] || "" },
      });
      toast.success("Upgrade ditolak");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal reject");
    }
  };

  const viewProof = (id) => {
    const token = getToken();
    window.open(`${API}/admin/upgrades/${id}/proof?auth=${encodeURIComponent(token)}`, "_blank");
  };

  const statusBadge = (s) => {
    const map = {
      pending: { cls: "bg-[#D99338]/15 text-[#A66A1F] border-[#D99338]/30", icon: Clock, text: "Pending" },
      approved: { cls: "bg-[#1B5E3F]/12 text-[#1B5E3F] border-[#1B5E3F]/30", icon: CheckCircle2, text: "Approved" },
      rejected: { cls: "bg-[#D14D4D]/12 text-[#D14D4D] border-[#D14D4D]/30", icon: XCircle, text: "Rejected" },
    };
    const m = map[s] || map.pending;
    const I = m.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${m.cls}`}>
        <I className="h-3.5 w-3.5" /> {m.text}
      </span>
    );
  };

  if (loading) return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#D16B4D]" /></div>;

  return (
    <Card className="border-[#EBE6DE]">
      <CardHeader className="flex-row items-center justify-between gap-4 flex-wrap">
        <CardTitle className="font-heading text-xl text-[#172E21]">
          Upgrade Requests ({items.length})
        </CardTitle>
        <div className="flex gap-2">
          {["pending", "approved", "rejected", "all"].map((s) => (
            <Button
              key={s}
              size="sm"
              variant={filter === s ? "default" : "outline"}
              data-testid={`filter-upgrade-${s}`}
              onClick={() => setFilter(s)}
              className={filter === s ? "bg-[#D16B4D] hover:bg-[#B85A3F]" : ""}
            >
              {s}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.length === 0 && (
          <div className="text-center py-12 text-[#586D61] text-sm">Tidak ada data</div>
        )}
        {items.map((u) => (
          <div
            key={u.id}
            data-testid={`upgrade-row-${u.id}`}
            className="rounded-xl border border-[#EBE6DE] bg-white p-4 space-y-3"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="font-heading text-base font-semibold text-[#172E21]">{u.tenant_name}</div>
                <div className="text-xs text-[#586D61]">{u.owner_email}</div>
                <div className="text-xs text-[#586D61] mt-1">Diajukan {formatDate(u.created_at)}</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-lg font-semibold text-[#D16B4D]">{formatIDR(u.amount)}</div>
                <div className="text-xs text-[#586D61]">
                  {u.period === "yearly" ? `${u.months} tahun` : `${u.months} bulan`}
                </div>
                <div className="mt-2">{statusBadge(u.status)}</div>
              </div>
            </div>
            {u.notes && (
              <div className="text-xs text-[#586D61] border-l-2 border-[#EBE6DE] pl-3">
                <span className="font-bold text-[#172E21]">Catatan: </span>{u.notes}
              </div>
            )}
            {u.admin_notes && (
              <div className="text-xs text-[#D14D4D] border-l-2 border-[#D14D4D]/30 pl-3">
                <span className="font-bold">Catatan admin: </span>{u.admin_notes}
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              {u.proof_storage_path && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`btn-view-proof-${u.id}`}
                  onClick={() => viewProof(u.id)}
                  className="gap-1.5"
                >
                  <Eye className="h-3.5 w-3.5" /> Lihat Bukti
                </Button>
              )}
              {u.status === "pending" && (
                <>
                  <Button
                    size="sm"
                    data-testid={`btn-approve-${u.id}`}
                    onClick={() => approve(u.id)}
                    className="gap-1.5 bg-[#1B5E3F] hover:bg-[#0F4D32]"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" /> Setujui & Aktifkan
                  </Button>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`btn-reject-${u.id}`}
                        className="gap-1.5 border-[#D14D4D]/40 text-[#D14D4D] hover:bg-[#D14D4D]/8"
                      >
                        <XCircle className="h-3.5 w-3.5" /> Tolak
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Tolak pengajuan?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Berikan alasan agar tenant memahami kenapa pengajuannya ditolak.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <Textarea
                        placeholder="Alasan penolakan…"
                        data-testid={`reject-notes-${u.id}`}
                        value={adminNotes[u.id] || ""}
                        onChange={(e) => setAdminNotes({ ...adminNotes, [u.id]: e.target.value })}
                      />
                      <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => reject(u.id)}
                          className="bg-[#D14D4D] hover:bg-[#A83A3A]"
                        >
                          Konfirmasi Tolak
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function PlatformSettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/platform/settings").then((r) => setS({ ...r.data, monthly_price_idr: r.data.monthly_price_idr, yearly_price_idr: r.data.yearly_price_idr }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/platform/settings", {
        bank_info: s.bank_info,
        monthly_price_idr: Number(s.monthly_price_idr),
        yearly_price_idr: Number(s.yearly_price_idr),
        platform_name: s.platform_name,
        contact_whatsapp: s.contact_whatsapp,
      });
      toast.success("Pengaturan platform tersimpan");
    } catch (e) {
      toast.error("Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#D16B4D]" /></div>;

  return (
    <Card className="border-[#EBE6DE]">
      <CardHeader>
        <CardTitle className="font-heading text-xl text-[#172E21]">Pengaturan Platform</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 max-w-2xl">
        <div>
          <Label>Nama Platform</Label>
          <Input
            data-testid="platform-name-input"
            value={s.platform_name}
            onChange={(e) => setS({ ...s, platform_name: e.target.value })}
            className="mt-2 border-[#EBE6DE]"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Harga Bulanan (IDR)</Label>
            <Input
              type="number"
              data-testid="monthly-price-input"
              value={s.monthly_price_idr}
              onChange={(e) => setS({ ...s, monthly_price_idr: e.target.value })}
              className="mt-2 border-[#EBE6DE] font-mono"
            />
          </div>
          <div>
            <Label>Harga Tahunan (IDR)</Label>
            <Input
              type="number"
              data-testid="yearly-price-input"
              value={s.yearly_price_idr}
              onChange={(e) => setS({ ...s, yearly_price_idr: e.target.value })}
              className="mt-2 border-[#EBE6DE] font-mono"
            />
          </div>
        </div>
        <div>
          <Label>Info Bank Transfer</Label>
          <Textarea
            data-testid="bank-info-input"
            rows={6}
            value={s.bank_info}
            onChange={(e) => setS({ ...s, bank_info: e.target.value })}
            className="mt-2 border-[#EBE6DE] font-mono text-sm"
          />
        </div>
        <div>
          <Label>Nomor WhatsApp Bantuan (62...)</Label>
          <Input
            data-testid="whatsapp-input"
            value={s.contact_whatsapp}
            onChange={(e) => setS({ ...s, contact_whatsapp: e.target.value })}
            className="mt-2 border-[#EBE6DE] font-mono"
            placeholder="6281234567890"
          />
        </div>
        <Button
          onClick={save}
          data-testid="btn-save-platform"
          disabled={saving}
          className="bg-[#D16B4D] hover:bg-[#B85A3F] gap-2 mt-2"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Simpan
        </Button>
      </CardContent>
    </Card>
  );
}

export default function SuperAdmin() {
  const navigate = useNavigate();
  const { isSuperAdmin, status } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (status === "authed" && !isSuperAdmin) navigate("/dashboard");
  }, [isSuperAdmin, status, navigate]);

  useEffect(() => {
    if (isSuperAdmin) api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [isSuperAdmin]);

  if (!isSuperAdmin) return null;

  return (
    <>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D16B4D] mb-2 flex items-center gap-2">
            <Shield className="h-3 w-3" /> Super Admin Panel
          </div>
          <h1 className="font-heading text-4xl lg:text-5xl font-medium text-[#172E21] tracking-tight">
            Platform Control
          </h1>
          <p className="text-[#586D61] mt-2 text-base">
            Kelola tenant, approve upgrade, dan atur konfigurasi platform.
          </p>
        </div>
        <Button
          variant="outline"
          data-testid="btn-back-app"
          onClick={() => navigate("/dashboard")}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-stats">
          <StatBox label="Total Tenant" value={stats.total_tenants} icon={Building2} accent="#082618" />
          <StatBox label="Aktif/Free" value={stats.active_tenants} icon={CheckCircle2} accent="#1B5E3F" />
          <StatBox label="Trial" value={stats.trial_tenants} icon={Clock} accent="#D99338" />
          <StatBox label="Expired" value={stats.expired_tenants} icon={XCircle} accent="#D14D4D" />
          <StatBox label="Pending" value={stats.pending_upgrades} icon={FileText} accent="#D16B4D" />
          <StatBox label="Revenue" value={formatIDR(stats.total_revenue_idr)} icon={TrendingUp} accent="#D16B4D" />
        </div>
      )}

      <Tabs defaultValue="tenants" className="w-full">
        <TabsList className="bg-[#EBE6DE]/50 p-1 rounded-xl">
          <TabsTrigger value="tenants" data-testid="tab-admin-tenants" className="data-[state=active]:bg-white rounded-lg px-5">
            <Users className="h-4 w-4 mr-1.5" /> Tenants
          </TabsTrigger>
          <TabsTrigger value="upgrades" data-testid="tab-admin-upgrades" className="data-[state=active]:bg-white rounded-lg px-5">
            <FileText className="h-4 w-4 mr-1.5" /> Upgrades
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-admin-settings" className="data-[state=active]:bg-white rounded-lg px-5">
            <Shield className="h-4 w-4 mr-1.5" /> Settings
          </TabsTrigger>
        </TabsList>
        <TabsContent value="tenants" className="mt-6"><TenantsTab /></TabsContent>
        <TabsContent value="upgrades" className="mt-6"><UpgradesTab /></TabsContent>
        <TabsContent value="settings" className="mt-6"><PlatformSettingsTab /></TabsContent>
      </Tabs>
    </>
  );
}
